import {
  Box,
  Card,
  CardActionArea,
  CardContent,
  Chip,
  Stack,
  Typography,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';

import { SURFACE, SURFACES } from '@/designs-dev/loft/accents';
import {
  PROJECTS,
  accentOf,
  projectHref,
  sourceOfTruth,
  type Project,
} from '@/designs-dev/loft/projects';

// The landing page: the app, then the projects. Every card opens a prototype.
//
// It was every prototype in the system behind three zone tabs, then briefly five
// area cards leading to pages that led to pages. Both were navigation for its
// own sake. A card is a way into the work, so it goes straight there.
const ProjectCard = ({ project }: { project: Project }) => {
  const navigate = useNavigate();
  const version = sourceOfTruth(project);
  const href = projectHref(project);
  const note =
    project.kind === 'master'
      ? 'The whole app'
      : version.screens.length === 0
        ? 'Nothing prototyped yet'
        : null;

  return (
    <Card variant='outlined'>
      <CardActionArea onClick={() => navigate(href)} disabled={href === '/'}>
        <Box sx={{ height: 6, bgcolor: accentOf(project) }} />
        <CardContent>
          <Stack spacing={1}>
            <Typography variant='h4'>{project.title}</Typography>
            <Typography variant='body2' color='text.secondary'>
              {project.blurb}
            </Typography>
            {/* No screen count. How many files sit under a project was never a
                reason to open it, and it read as progress when it is not. The
                two captions left both say something you would act on. */}
            <Stack sx={{ alignItems: 'center' }} direction='row' spacing={2}>
              {note && (
                <Typography variant='caption' color='text.secondary'>
                  {note}
                </Typography>
              )}
              {project.ticket && (
                <Chip size='small' variant='outlined' label={project.ticket} />
              )}
            </Stack>
          </Stack>
        </CardContent>
      </CardActionArea>
    </Card>
  );
};

const LoftHome = () => {
  const [app, ...projects] = PROJECTS;

  return (
    <Box sx={{ mx: 'auto', p: 5, maxWidth: 1100 }}>
      <Stack sx={{ mb: 4 }} spacing={1}>
        <Typography variant='h1'>Loft</Typography>
        <Typography variant='body1' color='text.secondary'>
          The app, and the {projects.length} projects being built into it.
        </Typography>
      </Stack>

      {/* The key. The accent on a card is derived from the surface a project
          touches, which only reads as meaning once something says so. */}
      <Stack
        sx={{ flexWrap: 'wrap', mb: 5 }}
        direction='row'
        spacing={3}
        useFlexGap
      >
        {SURFACES.map((s) => (
          <Stack
            sx={{ alignItems: 'center' }}
            key={s}
            direction='row'
            spacing={1}
          >
            <Box
              sx={{
                width: 12,
                height: 6,
                borderRadius: '2px',
                bgcolor: SURFACE[s].accent,
              }}
            />
            <Typography variant='caption' color='text.secondary'>
              {SURFACE[s].label}
            </Typography>
          </Stack>
        ))}
      </Stack>

      <Stack spacing={5}>
        {/* The app sits above the work, on its own, because it is not a project
            and opening it is the most common thing anyone does here. */}
        <ProjectCard project={app} />

        <Box
          sx={{
            display: 'grid',
            gap: 3,
            gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))',
          }}
        >
          {projects.map((p) => (
            <ProjectCard key={p.slug} project={p} />
          ))}
        </Box>
      </Stack>
    </Box>
  );
};

export default LoftHome;
